import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';

import { CSSObjectWithLabel } from 'react-select';

import DatePickerTag4 from '@/components/common/DatePicker/DatePickerTag4';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import { isValidDate, toMutationDateString } from '@/utils/date.utils';
import { UpdatePauseBillingFields, validationSchema } from './validation';
import { UpdateBillingParams } from '../../hooks/useUpdatePauseBilling';
import { GetReasonTypes } from '@/graphql/contracts/_generated_/getReasonTypes.query';
import { PAUSE_BILLING_TYPE } from '@/config/reasonTypes.config';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';

interface IUpdateModalProps {
  onClose: () => void;
  updatePauseBilling: (inputParams: UpdateBillingParams) => void;
  billingData: Contract['billing'];
  reasonCodes: ReasonCode[];
  reasonTypes: ReasonType[];
  isLoadingReasonCodes?: boolean;
}

export default function UpdateBillingModal({
  onClose,
  updatePauseBilling,
  billingData,
  reasonCodes,
  reasonTypes,
  isLoadingReasonCodes
}: IUpdateModalProps) {
  const [isWarningVisible, setIsWarningVisible] = useState<boolean>(false);
  const { values, setFieldValue, errors, isValid, dirty, setFieldTouched, touched }
    = useFormik<UpdatePauseBillingFields>({
      initialValues: {
        comment: billingData.statusUpdateComment,
        startDate: billingData.statusUpdated,
        endDate: billingData.currentStatusEndDate,
        reasonCode: billingData.reasonCode?.code ?? '',
        reference: billingData.statusUpdateReference,
      },
      validationSchema,
      onSubmit: () => undefined,
      validateOnMount: true,
    });

  // To show old reason inside dropdown even if it was already disabled
  // (user can accidentally change reason and want to return back old one)
  const reasonCodesToShowInDropdown = useMemo<ReasonCode[]>(() => {
    const reasons = [...reasonCodes];
    const isOldReasonAlreadyInDropdown = Boolean(reasons.find((el) => el.code === billingData.reasonCode?.code));

    if (!isOldReasonAlreadyInDropdown) {
      reasons.push(billingData.reasonCode!);
    }

    return reasons;
  }, [reasonCodes, billingData.reasonCode]);

  const onUpdatePauseBilling = () => {
    updatePauseBilling({
      endDate: values.endDate ? toMutationDateString(values.endDate) : undefined,
      reasonCode: values.reasonCode,
      comment: values.comment,
      reference: values.reference,
    });
  };

  if (isWarningVisible) {
    return (
      <AgConfirmationModal
        isOpen={true}
        title="You are about to modify terms for this contract"
        onClose={onClose}
        onConfirm={onUpdatePauseBilling}
        contentStyle={{ width: 680 }}
      >
        The contract is currently in a 'Billing Paused' state. You are about to modify the fields.
        <br />
        Please confirm to proceed further.
      </AgConfirmationModal>
    );
  }

  return (
    <AgModalWindow
      title="Modify Pause Billing"
      isOpen={true}
      onClose={onClose}
      contentStyle={{ width: 340 }}
    >
      <div className="mb-15">
        <ModalFormDropdown<GetReasonTypes['reasonTypes'][number]>
          id="reasonType"
          labelTitle="Reason Type"
          optionsData={reasonTypes}
          getOptionLabel={(option) => option.formattedType}
          getOptionValue={(option) => option.type}
          disabled
          defaultValue={reasonTypes.find((el) => el.type === PAUSE_BILLING_TYPE)}
        />
      </div>
      <div className="mb-15 mt-10">
        <ModalFormDropdown<ReasonCode>
          id="reasonCode"
          labelTitle="Reason Name"
          requiredIcon={true}
          isSearchable={true}
          optionsData={reasonCodesToShowInDropdown}
          placeholder="Select or type to search"
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.code}
          selectedOption={reasonCodesToShowInDropdown.find((el) => el.code === values.reasonCode) ?? null}
          menuPortalTarget={document.querySelector('body')}
          customStyles={{
            menuList: (provided) => ({ ...provided, maxHeight: 200 } as CSSObjectWithLabel)
          }}
          handleChange={(newValue) => {
            setFieldTouched('reasonCode', true, false);
            setFieldValue('reasonCode', newValue?.code);
          }}
          validationMessage={
            errors?.reasonCode && touched.reasonCode ? errors.reasonCode : undefined
          }
          isLoading={isLoadingReasonCodes}
          disabled={isLoadingReasonCodes}
        />
      </div>
      <div className="mb-15">
        <label htmlFor="startDate">Pause Billing Start Date</label>
        <DatePickerTag4
          id="startDate"
          disabled={true}
          value={isValidDate(new Date(values.startDate!)) ? new Date(values.startDate!) : undefined}
          onChange={() => { }}
        />
      </div>
      <div className="mb-15">
        <label htmlFor="endDate">Pause Billing End Date <span style={{ color: 'red' }}>*</span></label>
        <DatePickerTag4
          id="endDate"
          value={isValidDate(new Date(values.endDate!)) ? new Date(values.endDate!) : undefined}
          onChange={({ value }) => {
            setFieldTouched('endDate', true, false);
            setFieldValue('endDate', value);
          }}
        />
        {errors?.endDate && touched.endDate && (
          <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
            {errors.endDate}
          </div>
        )}
      </div>
      <div className="mb-15">
        <ModalFormTextarea
          id="reference"
          labelTitle="Reference"
          placeholder="Enter reference"
          value={values.reference ?? undefined}
          onChange={(event) => {
            setFieldTouched('reference', true, false);
            setFieldValue('reference', event.target.value);
          }}
          validationMessage={
            errors?.reference && touched.reference ? errors.reference : undefined
          }
        />
      </div>
      <div>
        <ModalFormTextarea
          id="comment"
          labelTitle="Comment"
          placeholder="Enter short comments"
          value={values.comment ?? undefined}
          onChange={(event) => {
            setFieldTouched('comment', true, false);
            setFieldValue('comment', event.target.value);
          }}
          validationMessage={
            errors?.comment && touched.comment ? errors.comment : undefined
          }
        />
      </div>
      <ActionButtonsWrapper
        leftButton={(
          <Button
            buttonStyleType={ButtonStyleType.SECONDARY}
            onClick={onClose}
          >
            Cancel
          </Button>
        )}
        rightButton={(
          <Button
            disabled={!isValid || !dirty}
            buttonStyleType={ButtonStyleType.PRIMARY}
            onClick={() => setIsWarningVisible(true)}
          >
            Save
          </Button>
        )}
      />
    </AgModalWindow>
  );
}
